<script setup lang="ts">
/**
 * Dialogue de clôture d'une demande : choix de l'issue (Gagnée / Perdue) + note.
 * Le rattachement des devis se fait à part (section dédiée de la fiche) ; ici on
 * vérifie seulement, via `canWin`, qu'une clôture en « Gagnée » est permise
 * (≥ 1 devis rattaché — câblé en 4b).
 */
import { computed, ref, watch } from 'vue'
import { TrophyIcon, HandThumbDownIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import type { AccompOutcome } from '@/v2/accompagnement/api/accompagnementTypes'

const props = defineProps<{
  open: boolean
  loading?: boolean
  /** Clôture en « Gagnée » autorisée (≥ 1 devis rattaché). Défaut : true. */
  canWin?: boolean
}>()
const emit = defineEmits<{ close: []; confirmed: [payload: { outcome: AccompOutcome; note: string }] }>()

const dialog = ref<HTMLDialogElement | null>(null)
const outcome = ref<AccompOutcome | null>(null)
const note = ref('')

watch(
  () => props.open,
  (open) => {
    if (open) {
      outcome.value = null
      note.value = ''
      if (!dialog.value?.open) dialog.value?.showModal()
    } else {
      dialog.value?.close()
    }
  },
)

/** « Gagnée » bloquée tant qu'aucun devis n'est rattaché (canWin === false). */
const winBlocked = computed(() => outcome.value === 'GAGNEE' && props.canWin === false)
const canConfirm = computed(() => outcome.value !== null && !winBlocked.value)

function confirm() {
  if (!canConfirm.value || !outcome.value) return
  emit('confirmed', { outcome: outcome.value, note: note.value.trim() })
}
</script>

<template>
  <dialog ref="dialog" class="modal" @close="emit('close')">
    <div class="modal-box">
      <h3 class="text-lg font-semibold">Clôturer la demande</h3>
      <p class="mt-1 text-sm text-base-content/70">
        Indiquez l'issue de l'accompagnement. Une action avec temps passé est requise.
      </p>

      <fieldset class="mt-4">
        <legend class="mb-2 text-sm font-medium text-base-content">
          Issue <span class="text-error" aria-hidden="true">*</span>
        </legend>
        <div class="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            class="flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 font-medium transition-colors"
            :class="
              outcome === 'GAGNEE'
                ? 'border-success bg-success/10 text-success'
                : 'border-base-300 text-base-content/80 hover:border-success/50'
            "
            :aria-pressed="outcome === 'GAGNEE'"
            @click="outcome = 'GAGNEE'"
          >
            <TrophyIcon class="h-5 w-5" aria-hidden="true" />
            Gagnée
          </button>
          <button
            type="button"
            class="flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 font-medium transition-colors"
            :class="
              outcome === 'PERDUE'
                ? 'border-error bg-error/10 text-error'
                : 'border-base-300 text-base-content/80 hover:border-error/50'
            "
            :aria-pressed="outcome === 'PERDUE'"
            @click="outcome = 'PERDUE'"
          >
            <HandThumbDownIcon class="h-5 w-5" aria-hidden="true" />
            Perdue
          </button>
        </div>
      </fieldset>

      <p
        v-if="winBlocked"
        role="alert"
        class="mt-3 flex items-start gap-2 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning"
      >
        <ExclamationTriangleIcon class="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        Rattachez au moins un devis à la demande pour pouvoir la clôturer en « Gagnée ».
      </p>

      <div class="mt-4">
        <label for="resolve-note" class="mb-1 block text-sm font-medium text-base-content">
          Note <span class="font-normal text-base-content/70">(optionnelle)</span>
        </label>
        <textarea
          id="resolve-note"
          v-model="note"
          rows="3"
          class="textarea textarea-bordered w-full text-base"
          placeholder="Conclusion, raison de la perte, suite donnée…"
        ></textarea>
      </div>

      <div class="modal-action">
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="emit('close')">Annuler</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canConfirm || loading"
          :aria-busy="loading"
          @click="confirm"
        >
          <span v-if="loading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          Clôturer
        </button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="emit('close')">Fermer</button>
    </form>
  </dialog>
</template>
